// Stable signed-URL reuse for GCS content.
//
// The backend window-signs objects so that the same object yields the same
// signed URL for a whole 7-day epoch (see fileHandler.ts `generateWindowedExpiryMs`).
// Objects also carry `Cache-Control: max-age=31536000, immutable` metadata.
// Caching the signed URLs here means subsequent page loads reuse the SAME
// URL, so the browser serves previously downloaded bytes from its HTTP cache
// instead of re-paying egress (a fresh signed URL per load would be a new
// cache key and force a re-download).
//
// localStorage footprint is capped: entries are evicted soonest-expiry-first.

const STORE_KEY = 'gcs_signed_url_cache';
const MAX_ENTRIES = 1000;

interface CachedSignedUrl {
	url: string;
	expiryMs: number;
}

type Store = Record<string, CachedSignedUrl>;

const readStore = (): Store => {
	try {
		const raw = localStorage.getItem(STORE_KEY);
		return raw ? (JSON.parse(raw) as Store) : {};
	} catch {
		return {};
	}
};

const writeStore = (store: Store): void => {
	try {
		localStorage.setItem(STORE_KEY, JSON.stringify(store));
	} catch {
		// Storage full/blocked — retry after dropping half of the entries.
		try {
			const entries = Object.entries(store)
				.sort(([, a], [, b]) => a.expiryMs - b.expiryMs)
				.slice(Math.floor(Object.keys(store).length / 2));
			localStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(entries)));
		} catch {
			// Give up silently — caching is best-effort only.
		}
	}
};

// v4 signed URLs embed their validity window:
//   X-Goog-Date=YYYYMMDDThhmmssZ  (validity start, UTC)
//   X-Goog-Expires=<seconds>      (validity duration from that start)
const parseSignedUrlExpiryMs = (signedUrl: string): number => {
	const dateMatch = /X-Goog-Date=(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/.exec(
		signedUrl
	);
	const expiresMatch = /X-Goog-Expires=(\d+)/.exec(signedUrl);
	if (!dateMatch || !expiresMatch) {
		// Non-GCS or unexpected URL — treat as very short-lived.
		return Date.now() + 5 * 60 * 1000;
	}
	const [, y, m, d, hh, mm, ss] = dateMatch;
	const startMs = Date.UTC(+y, +m - 1, +d, +hh, +mm, +ss);
	return startMs + (+expiresMatch[1]) * 1000;
};

// Cache key: the object URL without query (signatures are query-only), so all
// windows/signatures of one object map to the same entry.
const objectKeyOf = (signedUrl: string): string => signedUrl.split('?')[0];

const MIN_USABLE_VALIDITY_MS = 60 * 1000;

/**
 * Given an object's signed URL, return a URL that is stable within its
 * validity window: reuses the previously cached one when still valid, so the
 * browser can serve it from cache across page loads. Cache is best-effort.
 */
export const stableSignedUrl = (signedUrl: string): string => {
	if (!signedUrl) return signedUrl;
	const key = objectKeyOf(signedUrl);

	const store = readStore();
	const cached = store[key];
	const now = Date.now();

	if (cached && cached.expiryMs > now + MIN_USABLE_VALIDITY_MS) {
		return cached.url;
	}

	// Prune first to keep the store bounded.
	const keys = Object.keys(store);
	if (keys.length >= MAX_ENTRIES) {
		const victims = keys
			.sort((a, b) => store[a].expiryMs - store[b].expiryMs)
			.slice(0, Math.floor(MAX_ENTRIES / 4));
		for (const victim of victims) delete store[victim];
	}

	// Remember the freshly minted URL only if it is still worth caching.
	const expiryMs = parseSignedUrlExpiryMs(signedUrl);
	if (expiryMs > now + MIN_USABLE_VALIDITY_MS) {
		store[key] = { url: signedUrl, expiryMs };
		writeStore(store);
	}

	return signedUrl;
};